'use client';

import {
  PlanConfig,
  PlanTier,
  formatPrice,
  calculateTotalPrice,
  calculateSavingsPercent,
} from '@/app/config/plans';

// ============ Types ============

export type YearlyUpsellVariant = 'radio' | 'addRemove';

interface YearlyUpsellProps {
  /** The selected plan configuration */
  plan: PlanConfig;
  /** Currently selected number of years */
  selectedYears: number;
  /** Callback when years change */
  onYearsChange: (years: number) => void;
  /** Callback to return to plan selection */
  onChangePlan?: () => void;
  /** Whether the form is disabled (during processing) */
  disabled?: boolean;
  /** Which UX variant to display */
  variant?: YearlyUpsellVariant;
}

// ============ Radio Variant ============

interface RadioUpsellProps {
  plan: PlanConfig;
  selectedYears: number;
  onYearsChange: (years: number) => void;
  disabled?: boolean;
}

function RadioUpsell({ plan, selectedYears, onYearsChange, disabled }: RadioUpsellProps) {
  const yearOptions = [1, 2, 3];

  return (
    <div className="space-y-2">
      {yearOptions.map((years) => {
        const price = calculateTotalPrice(plan.tier, years);
        const savings = calculateSavingsPercent(plan.tier, years);
        const isSelected = selectedYears === years;

        return (
          <label
            key={years}
            className={`flex items-center justify-between p-3 border rounded cursor-pointer transition-colors ${
              isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center">
              <input
                type="radio"
                name="years"
                value={years}
                checked={isSelected}
                onChange={() => onYearsChange(years)}
                disabled={disabled}
                className="w-4 h-4 text-blue-500 border-slate-300 focus:ring-blue-500"
              />
              <span className="ml-3">
                {years} year{years > 1 ? 's' : ''} of {plan.displayName}
              </span>
              {savings > 0 && (
                <span className="ml-2 text-xs text-green-600 font-medium">Save {savings}%</span>
              )}
            </div>
            <span className="font-medium">{formatPrice(price)}</span>
          </label>
        );
      })}
    </div>
  );
}

// ============ Add/Remove Variant ============

interface AddRemoveUpsellProps {
  plan: PlanConfig;
  selectedYears: number;
  onYearsChange: (years: number) => void;
  onChangePlan?: () => void;
  disabled?: boolean;
}

/**
 * Calculate the price of adding one more year
 * For yearly plans: additional years are discounted (e.g., 50% off)
 */
function calculateNextYearPrice(tier: PlanTier, currentYears: number): number {
  // Price for adding one more year = total(current+1) - total(current)
  const currentTotal = calculateTotalPrice(tier, currentYears);
  const nextTotal = calculateTotalPrice(tier, currentYears + 1);
  return nextTotal - currentTotal;
}

/**
 * Calculate the discount percentage for adding the next year
 */
function calculateNextYearDiscount(tier: PlanTier, currentYears: number): number {
  const nextYearPrice = calculateNextYearPrice(tier, currentYears);
  const fullYearPrice = calculateTotalPrice(tier, 1); // Base price for 1 year
  const discount = ((fullYearPrice - nextYearPrice) / fullYearPrice) * 100;
  return Math.round(discount);
}

function AddRemoveUpsell({
  plan,
  selectedYears,
  onYearsChange,
  onChangePlan,
  disabled,
}: AddRemoveUpsellProps) {
  const minYears = 1;
  const maxYears = 5;

  const canRemove = selectedYears > minYears;
  const canAdd = selectedYears < maxYears;

  const nextYearDiscount = canAdd ? calculateNextYearDiscount(plan.tier, selectedYears) : 0;

  const handleAdd = () => {
    if (canAdd && !disabled) {
      onYearsChange(selectedYears + 1);
    }
  };

  const handleRemove = () => {
    if (canRemove && !disabled) {
      onYearsChange(selectedYears - 1);
    }
  };

  return (
    <div className="rounded overflow-clip border border-neutral-300">
      {/* Current selection row */}
      <div className="flex items-center justify-between p-3 bg-blue-50 border-b border-neutral-300">
        <div className="flex items-baseline gap-1 font-medium">
          <span>{plan.displayName}</span>
          {onChangePlan && (
            <button
              type="button"
              onClick={onChangePlan}
              disabled={disabled}
              className="text-xs font-normal text-zinc-800 underline transition-colors cursor-pointer hover:text-zinc-900 disabled:cursor-not-allowed"
            >
              Change
            </button>
          )}
        </div>
        <span className="font-medium">
          {selectedYears} year{selectedYears > 1 ? 's' : ''}
        </span>
      </div>

      {/* Add/Remove row */}
      <div className={`flex items-center justify-between p-3 ${disabled ? 'opacity-50' : ''}`}>
        <div className="flex items-center gap-2">
          {/* Plus button - only show if we can add */}
          <button
            type="button"
            onClick={handleAdd}
            disabled={disabled || canAdd === false}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Add a year"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-3.5 h-3.5"
            >
              <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
            </svg>
          </button>

          {/* Minus button - only show if we can remove */}
          {canRemove && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-600 text-white hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Remove a year"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-3.5 h-3.5"
              >
                <path
                  fillRule="evenodd"
                  d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}

          {/* Add year text */}
          {canAdd ? (
            <span className="text-sm text-zinc-800">
              <button
                type="button"
                onClick={handleAdd}
                disabled={disabled}
                className="font-medium text-zinc-800 hover:text-zinc-900 transition-colors cursor-pointer disabled:cursor-not-allowed"
                aria-label="Add a year"
              >
                Add an extra year
              </button>{' '}
              <span className="font-normal">and save {nextYearDiscount}%</span>
            </span>
          ) : (
            <span className="text-sm text-slate-500">Maximum of {maxYears} years reached</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Main Component ============

export default function YearlyUpsell({
  plan,
  selectedYears,
  onYearsChange,
  onChangePlan,
  disabled = false,
  variant = 'radio',
}: YearlyUpsellProps) {
  // Only render for plans that support multi-year
  if (!plan.supportsMultiYear) {
    return null;
  }

  if (variant === 'addRemove') {
    return (
      <AddRemoveUpsell
        plan={plan}
        selectedYears={selectedYears}
        onYearsChange={onYearsChange}
        onChangePlan={onChangePlan}
        disabled={disabled}
      />
    );
  }

  return (
    <RadioUpsell
      plan={plan}
      selectedYears={selectedYears}
      onYearsChange={onYearsChange}
      disabled={disabled}
    />
  );
}
